import { FC, ReactNode } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';

import {
  CheckCircleIcon,
  type Icon as PhosphorIcon,
  InfoIcon,
  SparkleIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react';

/**
 * The tinted "notice with an icon and an optional action" strip that had been
 * hand-rolled in several places (assessment completion, profile-completion
 * nudge, remediation CTA…). Same anatomy every time — tinted `Paper variant="panel"`,
 * a tone-coloured icon, a title/body pair, an optional action on the trailing
 * edge — so it lives here once instead of being re-derived per feature.
 *
 * Tone drives every colour; nothing else should. If a caller needs a colour the
 * tone map does not provide, add a tone rather than passing `sx` overrides —
 * that is what kept the previous copies drifting apart.
 */
export type CalloutTone = 'ai' | 'success' | 'warning' | 'error' | 'info' | 'neutral';

export type CalloutDensity = 'compact' | 'comfortable';

interface ToneStyle {
  background: string;
  border: string;
  /** Filled circle behind the icon. `comfortable` density only. */
  badge: string;
  icon: string;
  title: string;
  body: string;
  defaultIcon: PhosphorIcon;
}

function toneStyles(theme: Theme): Record<CalloutTone, ToneStyle> {
  const { palette } = theme;
  return {
    // The AI/remediation accent. `ocean` is the design system's intelligence
    // colour — deliberately distinct from brand blue and from every status hue,
    // so "a machine produced this" never reads as "this succeeded/failed".
    ai: {
      background: palette.ocean[100],
      border: palette.ocean[300],
      badge: palette.ocean[200],
      icon: palette.ocean[700],
      title: palette.ocean[900],
      body: palette.ocean[800],
      defaultIcon: SparkleIcon,
    },
    success: {
      background: palette.green[100],
      border: palette.grey[300],
      badge: palette.green[200],
      icon: palette.green[700],
      title: palette.green[800],
      body: palette.green[800],
      defaultIcon: CheckCircleIcon,
    },
    warning: {
      background: alpha(palette.warning.main, 0.1),
      border: alpha(palette.warning.main, 0.4),
      badge: alpha(palette.warning.main, 0.2),
      icon: palette.warning.main,
      title: palette.text.primary,
      body: palette.text.primary,
      defaultIcon: WarningIcon,
    },
    error: {
      background: alpha(palette.error.main, 0.08),
      border: alpha(palette.error.main, 0.35),
      badge: alpha(palette.error.main, 0.18),
      icon: palette.error.main,
      title: palette.text.primary,
      body: palette.text.secondary,
      defaultIcon: XCircleIcon,
    },
    info: {
      background: palette.blue[100],
      border: palette.blue[300],
      badge: palette.blue[200],
      icon: palette.blue[700],
      title: palette.text.primary,
      body: palette.text.secondary,
      defaultIcon: InfoIcon,
    },
    neutral: {
      background: palette.grey[100],
      border: palette.grey[300],
      badge: palette.grey[200],
      icon: palette.text.secondary,
      title: palette.text.primary,
      body: palette.text.secondary,
      defaultIcon: InfoIcon,
    },
  };
}

export interface CalloutBannerProps {
  tone?: CalloutTone;
  /** Overrides the tone's default icon. Pass a Phosphor component, not an element. */
  icon?: PhosphorIcon;
  /** Renders `weight="fill"` on the icon. Reads as more emphatic. */
  filledIcon?: boolean;
  title?: ReactNode;
  /** Body copy. On `compact` density this sits inline after the title. */
  children?: ReactNode;
  /** Trailing action, typically a `<Button variant="primary">`. */
  action?: ReactNode;
  /** Extra trailing content before the action — a progress bar, a count, … */
  trailing?: ReactNode;
  /**
   * `comfortable` (default) stacks title over body behind a circular icon badge.
   * `compact` is a single dense row for persistent page-level nudges.
   */
  density?: CalloutDensity;
  sx?: SxProps<Theme>;
}

export const CalloutBanner: FC<CalloutBannerProps> = observer(
  ({
    tone = 'neutral',
    icon,
    filledIcon = false,
    title,
    children,
    action,
    trailing,
    density = 'comfortable',
    sx,
  }) => {
    const theme = useTheme();
    const s = toneStyles(theme)[tone];
    const IconComponent = icon ?? s.defaultIcon;
    const compact = density === 'compact';

    return (
      <Paper
        variant="panel"
        elevation={0}
        sx={{
          backgroundColor: s.background,
          border: `1px solid ${s.border}`,
          ...(compact ? { py: 1.25, px: 2 } : null),
          ...sx,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: compact ? 'row' : { xs: 'column', md: 'row' },
            alignItems: compact ? 'center' : { xs: 'flex-start', md: 'center' },
            gap: compact ? 1.5 : 2,
          }}
        >
          {compact ? (
            <IconComponent
              size={20}
              color={s.icon}
              weight={filledIcon ? 'fill' : 'regular'}
            />
          ) : (
            <Box
              sx={{
                backgroundColor: s.badge,
                p: 2.125,
                borderRadius: '100%',
                display: 'flex',
                flexShrink: 0,
              }}
            >
              <IconComponent
                size={26}
                color={s.icon}
                weight={filledIcon ? 'fill' : 'regular'}
              />
            </Box>
          )}

          {compact ? (
            <Typography variant="body2" sx={{ flex: 1, color: s.body, minWidth: 0 }}>
              {title ? <strong>{title}</strong> : null}
              {title && children ? ' ' : null}
              {children}
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                flex: 1,
                minWidth: 0,
              }}
            >
              {title != null && (
                <Typography variant="textMD_medium" sx={{ color: s.title }}>
                  {title}
                </Typography>
              )}
              {children != null && (
                <Typography variant="textSM_regular" sx={{ color: s.body }}>
                  {children}
                </Typography>
              )}
            </Box>
          )}

          {trailing}

          {action != null && (
            <Box
              sx={{
                flexShrink: 0,
                alignSelf: compact ? 'center' : { xs: 'stretch', md: 'center' },
              }}
            >
              {action}
            </Box>
          )}
        </Box>
      </Paper>
    );
  },
);
